import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal, PageHero } from '../components/Shared';
import { publicApi, mediaUrl } from '../lib/publicApi';
import { useSeo } from '../hooks/useSeo';

export const CaseStudyCard = ({ study }) => (
  <Link to={`/case-studies/${study.slug}`} className="study-card" data-testid={`study-card-${study.slug}`}>
    <div className="study-card-cover">
      {study.cover_image ? <img src={mediaUrl(study.cover_image)} alt={study.client_name} loading="lazy" /> : <span className="post-card-cover-empty" />}
      {study.client_logo && <img className="study-card-logo" src={mediaUrl(study.client_logo)} alt={`${study.client_name} logo`} loading="lazy" />}
    </div>
    <div className="study-card-body">
      <span className="study-card-industry">{study.industry}</span>
      <h3>{study.client_name}</h3>
      {study.headline_metric && <strong className="study-card-metric">{study.headline_metric}</strong>}
      <p>{study.summary}</p>
      <span className="post-card-more">Read the case study <ArrowUpRight size={15} /></span>
    </div>
  </Link>
);

export default function CaseStudies() {
  const [industry, setIndustry] = useState('');
  const [pillar, setPillar] = useState('');
  const [data, setData] = useState({ items: [], industries: [], pillars: [] });
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Case Studies — TechSpitze',
    description: 'Measured outcomes from TechSpitze engagements across logistics, healthcare, financial services and more.',
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    publicApi.caseStudies(new URLSearchParams({ industry, pillar }).toString())
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setData({ items: [], industries: [], pillars: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [industry, pillar]);

  return <main id="main-content" className="studies-page" data-testid="case-studies-page">
    <PageHero id="studies-hero" eyebrow="PROOF, NOT PROMISES" lines={[{ text: 'Case Studies' }]} description="What changed, by how much, and how we got there — with the numbers clients agreed to publish." />

    <section className="container blog-controls">
      <Reveal className="blog-chips">
        <button type="button" className={`blog-chip ${!industry ? 'is-on' : ''}`} onClick={() => setIndustry('')} data-testid="study-industry-all">All industries</button>
        {data.industries.map(item => <button type="button" key={item} className={`blog-chip ${industry === item ? 'is-on' : ''}`} onClick={() => setIndustry(item)} data-testid={`study-industry-${item.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{item}</button>)}
      </Reveal>
      <Reveal className="blog-chips" delay={.08}>
        <button type="button" className={`blog-chip ${!pillar ? 'is-on' : ''}`} onClick={() => setPillar('')} data-testid="study-pillar-all">All services</button>
        {data.pillars.map(item => <button type="button" key={item} className={`blog-chip ${pillar === item ? 'is-on' : ''}`} onClick={() => setPillar(item)} data-testid={`study-pillar-${item.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{item}</button>)}
      </Reveal>
    </section>

    <section className="container blog-listing">
      {loading
        ? <div className="post-grid" data-testid="studies-loading">{[...Array(3)].map((_, i) => <div className="post-skeleton" key={i} />)}</div>
        : data.items.length === 0
          ? <div className="content-empty" data-testid="studies-empty"><h3>No case studies yet</h3><p>{industry || pillar ? 'Nothing matches that filter yet.' : 'Published case studies will appear here.'}</p></div>
          : <div className="post-grid study-grid">{data.items.map(study => <Reveal key={study.slug}><CaseStudyCard study={study} /></Reveal>)}</div>}
    </section>
  </main>;
}
